import { Trans, useI18n } from '@i18n';
import { Button } from '@mui/material';
import { PropsWithChildren, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { Features, useSession } from '@/components/session/useSession';
import usePosthogTracking from '@/hooks/usePosthogTracking';

type Props = {
  feature?: Features;
};

/**
 * A generic permission wrapper for a particular page that requires admin access.
 * The user can still use other parts of the application.
 */
export default function AdminAccessRequired({ children, feature }: PropsWithChildren<Props>) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasFeatureWriteAccess, isAdmin } = useSession();
  const { trackEvent } = usePosthogTracking();

  const hasAccess = isAdmin || (feature ? hasFeatureWriteAccess(feature) : false);

  useEffect(() => {
    if (!hasAccess) {
      trackEvent('ADMIN_PERMISSION_WALL_HIT', { path: location.pathname });
    }
  }, [hasAccess, location.pathname, trackEvent]);

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '2rem',
        // respecting the padding of the main component
        height: 'calc(100vh - 84px)',
      }}
    >
      <Iconify icon={ICONS.CARBON_LOCKED} width={64} height={64} />
      <PTypography variant={'h3'}>{t('General_MissingPermissions-title')}</PTypography>
      <PTypography variant={'body1'} align="center" sx={{ maxWidth: 600 }}>
        <Trans i18nKey="General_MissingPermissions-subtitle" />
      </PTypography>

      <Button
        variant="contained"
        onClick={() => {
          navigate(-1);
        }}
      >
        {t('General_ReturnToPreviousPage')}
      </Button>
    </div>
  );
}
